import { Check, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ProvisioningJob, ProvisioningJobStatus } from '@/services/provisioning';

type StepState = 'pending' | 'active' | 'done' | 'failed';

const STEPS = ['Dispatch', 'Run playbook', 'Verify domains', 'Live'] as const;

/**
 * Where the run has got to.
 *
 * This is the most valuable piece of UI here, because "the playbook succeeded
 * and we are waiting on Let's Encrypt" and "it is hung" look identical
 * otherwise — and the wait is routinely a couple of minutes.
 */
function stepStates(status: ProvisioningJobStatus): StepState[] {
    switch (status) {
        case 'DISPATCHING':
            return ['active', 'pending', 'pending', 'pending'];
        case 'DISPATCH_FAILED':
            return ['failed', 'pending', 'pending', 'pending'];
        case 'QUEUED':
            return ['done', 'pending', 'pending', 'pending'];
        case 'RUNNING':
            return ['done', 'active', 'pending', 'pending'];
        case 'FAILED':
        case 'TIMED_OUT':
        case 'CANCELLED':
            return ['done', 'failed', 'pending', 'pending'];
        case 'SUCCEEDED':
        case 'VERIFYING':
            return ['done', 'done', 'active', 'pending'];
        case 'VERIFY_FAILED':
            return ['done', 'done', 'failed', 'pending'];
        case 'VERIFIED':
            return ['done', 'done', 'done', 'done'];
    }
}

interface Props {
    job: ProvisioningJob;
    liveStatus?: ProvisioningJobStatus;
    verifyAttempt?: number;
    maxVerifyAttempts?: number;
}

export function ProvisioningStatusHeader({
    job,
    liveStatus,
    verifyAttempt,
    maxVerifyAttempts = 12,
}: Props) {
    const status = liveStatus ?? job.status;
    const states = stepStates(status);
    const attempt = verifyAttempt ?? job.verifyAttempt;

    return (
        <div className="space-y-3">
            <div className="flex items-center">
                {STEPS.map((label, index) => {
                    const state = states[index];
                    return (
                        <div key={label} className="flex flex-1 items-center last:flex-none">
                            <div className="flex flex-col items-center gap-1.5">
                                <div
                                    className={cn(
                                        'flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-medium',
                                        state === 'done' &&
                                        'border-emerald-500 bg-emerald-500 text-white',
                                        state === 'active' &&
                                        'border-blue-500 bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
                                        state === 'failed' &&
                                        'border-red-500 bg-red-500 text-white',
                                        state === 'pending' &&
                                        'border-slate-200 text-slate-400 dark:border-slate-700',
                                    )}
                                >
                                    {state === 'done' ? (
                                        <Check className="h-4 w-4" />
                                    ) : state === 'failed' ? (
                                        <X className="h-4 w-4" />
                                    ) : state === 'active' ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        index + 1
                                    )}
                                </div>
                                <span
                                    className={cn(
                                        'whitespace-nowrap text-xs',
                                        state === 'pending'
                                            ? 'text-slate-400'
                                            : 'text-slate-700 dark:text-slate-300',
                                    )}
                                >
                                    {label}
                                </span>
                            </div>
                            {index < STEPS.length - 1 && (
                                <div
                                    className={cn(
                                        'mx-2 h-0.5 flex-1',
                                        states[index + 1] === 'pending'
                                            ? 'bg-slate-200 dark:bg-slate-700'
                                            : 'bg-emerald-500',
                                    )}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            {status === 'VERIFYING' && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                    The playbook finished. Waiting for Caddy to obtain TLS certificates —
                    check {Math.max(attempt, 1)} of {maxVerifyAttempts}. This normally takes
                    a minute or two.
                </p>
            )}
        </div>
    );
}
